import { useEffect, useState } from 'react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { api } from '../lib/api.js';

export type Range = '1h' | '6h' | '24h' | '7d';
export const RANGES: Range[] = ['1h', '6h', '24h', '7d'];

export interface Sample {
  ts: string; // ISO from API
  [k: string]: number | string | boolean | null;
}

interface Props {
  endpoint: string;
  range: Range;
  dataKey: string;
  label: string;
  /** Color override; defaults to the brand emerald. */
  stroke?: string;
  /** y-axis upper bound (e.g. 100 for percentages). */
  max?: number;
  /** Number formatter for the live header value + tooltip. */
  format?: (n: number | null | undefined) => string;
  /** Compact mode shrinks margins for dense grids. */
  compact?: boolean;
}

/**
 * Single-metric area chart fed by `/api/metrics/...`. Re-fetches when the
 * endpoint or range changes; refreshes every 30s while mounted to stay in
 * step with the recorder's flush cadence.
 */
export function MetricChart({ endpoint, range, dataKey, label, stroke = '#34d399', max, format, compact }: Props) {
  const [samples, setSamples] = useState<Sample[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      api.get<{ samples: Sample[] }>(`${endpoint}?range=${range}`)
        .then((r) => { if (!cancelled) { setSamples(r.samples); setError(null); } })
        .catch((err) => { if (!cancelled) setError(err.message); });
    };
    load();
    const t = setInterval(load, 30_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [endpoint, range]);

  const last = samples && samples.length > 0 ? samples[samples.length - 1][dataKey] : null;
  const fmt = format ?? defaultFmt;
  const lastDisplay = typeof last === 'number' ? fmt(last) : '—';
  const gradId = `grad-${dataKey}-${stroke.replace('#', '')}`;

  return (
    <div className="card p-4">
      <div className="flex items-baseline justify-between mb-2">
        <span className="text-[10px] uppercase tracking-wider text-fg-dim font-semibold">{label}</span>
        <span className="text-lg font-semibold tabular-nums text-fg tracking-tight">{lastDisplay}</span>
      </div>
      <div className={compact ? 'h-20' : 'h-32'}>
        {error ? (
          <div className="h-full flex items-center justify-center text-xs text-status-error">{error}</div>
        ) : samples === null ? (
          <div className="h-full flex items-center justify-center text-xs text-fg-dim">Loading…</div>
        ) : samples.length === 0 ? (
          <div className="h-full flex items-center justify-center text-xs text-fg-dim italic">No data in range</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={samples} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={stroke} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={stroke} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#262b3a" strokeDasharray="2 4" vertical={false} />
              <XAxis
                dataKey="ts"
                tick={{ fontSize: 10, fill: '#646b7a' }}
                tickLine={false}
                axisLine={{ stroke: '#262b3a' }}
                tickFormatter={(v) => fmtTime(v, range)}
                minTickGap={30}
              />
              <YAxis
                tick={{ fontSize: 10, fill: '#646b7a' }}
                tickLine={false}
                axisLine={false}
                domain={max != null ? [0, max] : ['auto', 'auto']}
                width={48}
                tickFormatter={(v) => (typeof v === 'number' ? fmt(v) : v)}
              />
              <Tooltip
                contentStyle={{ background: '#161a24', border: '1px solid #262b3a', borderRadius: 8, fontSize: 11, padding: '6px 10px' }}
                labelFormatter={(v) => new Date(v as string).toLocaleString()}
                formatter={(v: number) => [fmt(v), label]}
              />
              <Area
                type="monotone"
                dataKey={dataKey}
                stroke={stroke}
                strokeWidth={1.75}
                fill={`url(#${gradId})`}
                isAnimationActive={false}
                connectNulls
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

export function RangePicker({ value, onChange }: { value: Range; onChange: (r: Range) => void }) {
  return (
    <div className="tab-bar !p-0.5 inline-flex">
      {RANGES.map((r) => (
        <button
          key={r}
          type="button"
          className={`tab-item !h-7 !px-2.5 !text-xs ${value === r ? 'tab-item-active' : ''}`}
          onClick={() => onChange(r)}
          aria-pressed={value === r}
        >
          {r}
        </button>
      ))}
    </div>
  );
}

function defaultFmt(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  if (Math.abs(n) >= 1000) return short(n);
  return n.toFixed(n % 1 === 0 ? 0 : 1);
}

function short(n: number): string {
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return `${Math.round(n)}`;
}

function fmtTime(iso: string, range: Range): string {
  const d = new Date(iso);
  if (range === '1h' || range === '6h') {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  if (range === '24h') {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}
